import { BarraDeLista, BuscaDaBarra, FiltrosAtivos } from '../BarraDeLista';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import {
  Aviso, Campo, Carregando, Dialogo, ErroDaApi, Escolha, Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';
import { useSessao } from '../sessao';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';

/**
 * Locais de armazenamento.
 *
 * ── Por que esta tela existe ─────────────────────────────────────────
 *
 * Porque, em homologação, **10 de 10 amostras diziam "sem local
 * registrado"**. Não era falha de layout: registrar onde a amostra ficou
 * dependia de alguém lembrar de abrir um formulário de seis botões, e
 * ninguém lembra. A onda 24 passou a derivar do ensaio tudo o que dá para
 * derivar; o que sobra — *onde ela foi guardada* — precisa de um lugar
 * para escolher, e escolher exige cadastro.
 *
 * Com texto livre, `camara umida`, `Câmara Úmida` e `CU` viram três
 * lugares e o inventário do laboratório nunca fecha.
 *
 * ── O que a coluna «Guarda» responde ─────────────────────────────────
 *
 * `armazenadas` é o que está fisicamente ali agora. `amostras` inclui as
 * que saíram para ensaio e ainda pertencem a este lugar — é o número que
 * responde «o que está fora da prateleira 3?», que é a pergunta de quem
 * vai procurar e não acha.
 */

interface Local {
  id: string;
  codigo: string;
  nome: string;
  tipo: string;
  tipo_nome: string;
  descricao: string | null;
  situacao: string;
  ordem: number;
  amostras: number;
  armazenadas: number;
}

interface Tipo {
  codigo: string; nome: string; descricao: string; ordem: number;
}

const TOM: Record<string, Tom> = {
  ativo: 'ok', inativo: 'neutro', bloqueado: 'critico',
};

const SITUACAO: Record<string, string> = {
  ativo: 'Ativo', inativo: 'Inativo', bloqueado: 'Bloqueado',
};

export function Locais() {
  const [linhas, setLinhas] = useState<Local[] | null>(null);
  const [tipos, setTipos] = useState<Tipo[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [novo, setNovo] = useState(false);

  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (busca) q.set('busca', busca);
      setLinhas(await api<Local[]>(`/locais?${q}`));
    } catch (e) { setErro(e); }
  }, [busca]);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, 250);
    return () => clearTimeout(t);
  }, [carregar]);

  useEffect(() => {
    api<Tipo[]>('/locais/tipos').then(setTipos).catch(() => setTipos([]));
  }, []);

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Locais de armazenamento</h1>
          <p className="sub">
            Onde a amostra fica — prateleira, câmara úmida, bancada
          </p>
        </div>
        {pode('cadastro.editar') && (
          <button type="button" className="botao" onClick={() => setNovo(true)}>
            Novo local
          </button>
        )}
      </div>

      {novo && (
        <FormLocal
          tipos={tipos}
          aoFechar={() => setNovo(false)}
          aoPronto={() => { setNovo(false); void carregar(); }}
        />
      )}

      <BarraDeLista rotulo="Filtros dos locais">
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Código, nome ou descrição" rotulo="Buscar local" />
      </BarraDeLista>
      <FiltrosAtivos
        itens={busca ? [{ chave: 'q', texto: `Busca: ${busca}`, tirar: () => setBusca('') }] : []}
        aoLimpar={() => setBusca('')}
        contagem={linhas ? `${linhas.length} ${linhas.length === 1 ? 'local' : 'locais'} · na ordem do laboratório` : undefined} />

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : (
          <div className="cartao">
            <div className="cartao-cabeca">
              <h2>Cadastro</h2>
              <span className="sub">{linhas.length} local(is)</span>
            </div>

            {linhas.length === 0 ? (
              <Vazio>
                Nenhum local cadastrado. O sistema não inventa prateleira —
                cadastre os lugares do laboratório e a coluna «onde está»
                das amostras para de ficar vazia.
              </Vazio>
            ) : (
              /*
               * Os locais numa tabela com ficha (onda 219): o local, o que
               * ele guarda e a situação ficam; o tipo é detalhe e, com a
               * ficha aberta, mora nela.
               */
              <TabelaComFicha
                linhas={linhas}
                chave={(l) => l.id}
                nome={(l) => `Local ${l.nome}`}
                rotulo="Locais de armazenamento"
                colunas={COLUNAS_DO_LOCAL}
                ficha={fichaDoLocal}
              />
            )}

            <p className="rodape-regra">
              Local não se exclui: um lugar que já guardou amostra continua
              no histórico dela. O que muda é a situação — inativo e
              bloqueado somem da lista de escolha sem sumir da cadeia de
              custódia de quem passou por ali.
            </p>
          </div>
        )}
    </div>
  );
}

function FormLocal({
  tipos, aoFechar, aoPronto,
}: { tipos: Tipo[]; aoFechar(): void; aoPronto(): void }) {
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState<string | null>(null);
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const escolhido = tipos.find((t) => t.codigo === tipo);

  const enviar = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      await api('/locais', {
        metodo: 'POST',
        corpo: {
          codigo: codigo.trim(),
          nome: nome.trim(),
          tipo,
          descricao: descricao.trim() || undefined,
        },
      });
      aoPronto();
    } catch (x) {
      setErro(x);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialogo aberto titulo="Novo local" aoFechar={aoFechar}>
      <form onSubmit={enviar}>
        <ErroDaApi erro={erro} />

        <Campo
          rotulo="Nome"
          value={nome}
          onChange={(e) => setNome(e.currentTarget.value)}
          dica="Como a pessoa chama o lugar: “Prateleira A-3”, “Câmara úmida 1”."
          autoFocus
          required
        />

        <Campo
          rotulo="Código"
          value={codigo}
          onChange={(e) => setCodigo(e.currentTarget.value)}
          dica="Curto, para caber na etiqueta e na conversa: PRAT-A3, CU-1."
          required
        />

        <Escolha
          rotulo="Tipo"
          opcoes={tipos.map((t) => ({ valor: t.codigo, texto: t.nome }))}
          valor={tipo}
          aoEscolher={setTipo}
          limpavel={false}
          dica={escolhido?.descricao
            ?? 'O tipo diz o que aquele lugar preserva, e é o que agrupa a lista.'}
        />

        {tipo === 'estufa' && (
          <Aviso tipo="atencao" titulo="Estufa é processo, não guarda.">
            Amostra em estufa está secando, não repousando. Se o material
            fica ali por dias entre um ensaio e outro, o lugar de guarda é
            outro.
          </Aviso>
        )}

        <Campo
          rotulo="Descrição"
          value={descricao}
          onChange={(e) => setDescricao(e.currentTarget.value)}
          dica="Opcional. Onde fica, o que cabe, o que não pode ir ali."
        />

        <div className="linha" style={{ marginTop: '1rem' }}>
          <button type="button" className="botao secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button
            type="submit"
            className="botao"
            disabled={enviando || !nome.trim() || !codigo.trim() || !tipo}
          >
            {enviando ? 'Gravando…' : 'Cadastrar'}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

/**
 * O que o local guarda. «Fora, em uso» é o que saiu e ainda é daqui — a
 * pergunta de quem vai procurar e não acha; sem ela, a resposta seria
 * «sumiu».
 */
function Guarda({ l }: { l: Local }) {
  return (
    <>
      {l.armazenadas > 0
        ? <Link to={`/amostras?local=${l.id}`}>{l.armazenadas} guardada(s)</Link>
        : <span className="nada">vazio</span>}
      {l.amostras > l.armazenadas && (
        <span className="sub">{l.amostras - l.armazenadas} fora, em uso</span>
      )}
    </>
  );
}

const COLUNAS_DO_LOCAL: ColunaComFicha<Local>[] = [
  {
    chave: 'local',
    cabeca: <th scope="col">Local</th>,
    celula: (l) => (
      <th scope="row">
        <strong>{l.nome}</strong>
        <span className="sub">{l.codigo}</span>
        {l.descricao && <span className="sub">{l.descricao}</span>}
      </th>
    ),
  },
  {
    chave: 'tipo', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">Tipo</th>,
    celula: (l) => <td className="col-opcional">{l.tipo_nome}</td>,
  },
  { chave: 'guarda', cabeca: <th scope="col">Guarda</th>, celula: (l) => <td><Guarda l={l} /></td> },
  {
    chave: 'situacao',
    cabeca: <th scope="col">Situação</th>,
    celula: (l) => (
      <td><Selo tom={TOM[l.situacao] ?? 'neutro'}>{SITUACAO[l.situacao] ?? l.situacao}</Selo></td>
    ),
  },
];

function fichaDoLocal(l: Local): FichaDaLinha {
  return {
    rotulo: 'Local de armazenamento',
    titulo: l.nome,
    sub: <span className="mono">{l.codigo}</span>,
    corpo: (
      <>
        <div className="tf-selos">
          <Selo tom={TOM[l.situacao] ?? 'neutro'}>{SITUACAO[l.situacao] ?? l.situacao}</Selo>
        </div>
        <CamposDaFicha itens={[
          ['Tipo', l.tipo_nome],
          ['Descrição', l.descricao],
          ['Guarda', <Guarda l={l} />],
        ]} />
      </>
    ),
    acoes: l.armazenadas > 0
      ? <Link className="botao" to={`/amostras?local=${l.id}`}>Ver as amostras daqui</Link>
      : undefined,
  };
}
